import { IProfile } from '@jumpstart/core';
import CloseIcon from '@mui/icons-material/Close';
import PaidIcon from '@mui/icons-material/Paid';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import React, { useState } from 'react';
import { useAdminProfile } from '../../../core/hooks';
import { SCOUT9_COLORS } from '../../../core/theme';
import { ISubscriber } from '../schema';
import ServicePaymentMessage from './ServicePaymentMessage';
import { quickInvoice } from './services/Payments';
import { IServiceOption } from './services/service-options';
import { dateStr, sendMessage } from './services/Texting';


const styles: any = {
  root: {
    // padding: '4px 8px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    backgroundColor: SCOUT9_COLORS.gray6,
    minHeight: 62,
    '& > :not(:last-child)': {
      marginRight: 2
    }
  },
  input: {
    backgroundColor: SCOUT9_COLORS.white,
    marginLeft: 1,
    flex: 1,
    minHeight: 42,
    borderRadius: '8px',
    padding: '4px 14px 5px'
  },
  iconButton: {
    padding: '20px'
  },
  divider: {
    height: 28,
    margin: 4
  }
};

export interface IMessageBar {
  subscriber: ISubscriber;
  subscriberProfile: IProfile;
  onSuccessfulSend(): void;
}

export default function MessageBar(props: IMessageBar) {
  const { subscriber, subscriberProfile, onSuccessfulSend } = props;
  const [body, setBody] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [sending, setSending] = useState(false);
  const [sendingPayment, setSendingPayment] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const adminProfile = useAdminProfile();

  const handlePaymentSubmit = (service: IServiceOption) => {
    setPaymentOpen(false);
    setSendingPayment(true);
    if (!subscriberProfile) {
      throw new Error('No profile information provided');
    }
    const customerIDKey = process.env.NODE_ENV === 'production' ? 'stripeCustomerId' : 'stripeCustomerIdDev';
    if (!subscriberProfile.payment) {
      throw new Error('User has profile no payment information (@TODO create stripe customer object if its missing)');
    }
    if (!(subscriberProfile.payment as any)[customerIDKey]) {
      throw new Error('User has profile no payment information (@TODO create stripe customer object if its missing)');
    }
    quickInvoice({
      customerId: (subscriberProfile.payment as any)[customerIDKey],
      productId: service.productId,
      priceId: service.priceId,
      description: service.description,
      emblem: adminProfile && adminProfile.firstName ? adminProfile.firstName : 'WelcomeHome House Manager'
    })
      .then(() => {
        setSendingPayment(false);
        onSuccessfulSend();
      })
      .catch((err: Error) => {
        setError(err);
        setSendingPayment(false);
      });
  };

  const handleSubmit = () => {
    setSending(true);
    const todayDate: moment.Moment = moment();
    const today = dateStr(todayDate.toDate());
    sendMessage({
      uid: subscriber.uid,
      body: body,
      phone: subscriber.phone,
      messageID: today,
      emblem: adminProfile && adminProfile.firstName ? adminProfile.firstName : 'WelcomeHome House Manager'
    })
      .then(() => {
        onSuccessfulSend();
        setBody('');
        setSending(false);
      })
      .catch((err: Error) => {
        setError(err);
        setSending(false);
      });
  };

  return (
    <React.Fragment>
      {!!error ? <Alert severity="error" onClose={() => {
        setError(null);
      }}>{error.message}</Alert> : null}
      <Paper sx={styles.root} elevation={0}>
        <IconButton sx={styles.iconButton} aria-label="search" onClick={() => setPaymentOpen(true)}>
          {sendingPayment ? <CircularProgress size={24}/> : <PaidIcon/>}
        </IconButton>
        <InputBase
          sx={styles.input}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setBody(event.target.value);
          }}
          placeholder="Message"
          multiline
          value={body}
        />
        <IconButton sx={styles.iconButton} aria-label="search" onClick={handleSubmit}>
          {sending ? <CircularProgress size={24}/> : <SendIcon/>}
        </IconButton>
      </Paper>

      <Dialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
      >
        <DialogTitle>
          Send Service payment
          <IconButton
            size="small"
            onClick={() => setPaymentOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon/>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ServicePaymentMessage subscriberProfile={subscriberProfile} onSubmit={handlePaymentSubmit}/>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
